/**
 * Print form workspace (an IN-APP tab, 1C-style): paged A4 preview, page
 * setup, cell editing, browser print / PDF / Excel. Everything derives from a
 * single spreadsheet-document model (possibly edited). Descriptor-driven;
 * hosted by the App workspace as an ordinary tab or on the /print route
 * (deep link).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Slider from '@mui/material/Slider';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmailIcon from '@mui/icons-material/Email';

import { CommandBar } from '@/shared/components/CommandBar';
import { printFormsApi } from '@/utils/api';
import { SpreadsheetView } from './spreadsheet/SpreadsheetView';
import type { FilledSpreadsheetModel, PageSettings } from './spreadsheet/types';
import type { PrintWindowDescriptor, PrintWindowLanguage } from './openPrintWindow';
import { formatPrintWindowTitle } from './formatPrintWindowTitle';
import { printErrorMessage } from './usePrintCommands';
import { usePrintAttachments } from './usePrintAttachments';
import { PrintAttachmentsPanel } from './PrintAttachmentsPanel';
import { PrintAttachmentPreview } from './PrintAttachmentPreview';
import { EmailPrintDialog } from './EmailPrintDialog';

function downloadBase64(content: string, filename: string, mime: string): void {
  const bytes = atob(content);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export interface PrintWindowViewProps {
  descriptor: PrintWindowDescriptor;
  /** Tab title: `Print — {presentation}` (once the model has loaded). */
  onTitleChange?: (title: string) => void;
}

export function PrintWindowView({ descriptor, onTitleChange }: PrintWindowViewProps) {
  const { t } = useTranslation();
  const connectionId = descriptor.connectionId;
  const tableName = descriptor.tableName;
  const instanceId = descriptor.instanceId;
  const layoutNodeId = descriptor.layoutNodeId;

  const [model, setModel] = useState<FilledSpreadsheetModel | null>(null);
  const [recordLabel, setRecordLabel] = useState('');
  const [locale, setLocale] = useState<string | null>(descriptor.locale ?? null);
  const [languages, setLanguages] = useState<PrintWindowLanguage[]>(
    descriptor.languages ?? [],
  );
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [zoomPct, setZoomPct] = useState(100);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [pageSetupOpen, setPageSetupOpen] = useState(false);
  const printAttachments = usePrintAttachments({ connectionId, tableName, instanceId });
  const [attachmentsPanelOpen, setAttachmentsPanelOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const printFrameRef = useRef<HTMLIFrameElement>(null);
  const onTitleChangeRef = useRef(onTitleChange);
  onTitleChangeRef.current = onTitleChange;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!connectionId || !tableName || !instanceId || !layoutNodeId) {
      setError(tRef.current('printWindow.badLink', 'Invalid print form link'));
      return;
    }

    // Pre-filled from POST .../print — skip the legacy spreadsheetDesigner fill.
    if (descriptor.model) {
      setError(null);
      setModel(descriptor.model);
      setLocale(descriptor.locale ?? null);
      setLanguages(descriptor.languages ?? []);
      const label = descriptor.title || descriptor.model.title || instanceId;
      setRecordLabel(label);
      onTitleChangeRef.current?.(formatPrintWindowTitle(label));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = (await printFormsApi.spreadsheetDesigner({
          connection_id: connectionId,
          table_name: tableName,
          instance_id: instanceId,
          layout_node_id: layoutNodeId,
        })) as {
          model: FilledSpreadsheetModel;
          record_label?: string;
          locale?: string | null;
          languages?: PrintWindowLanguage[];
        };
        if (cancelled) return;
        setModel(data.model);
        setLocale(data.locale ?? null);
        setLanguages(data.languages ?? []);
        const label = data.record_label || data.model.title || instanceId;
        setRecordLabel(label);
        onTitleChangeRef.current?.(formatPrintWindowTitle(label));
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    connectionId,
    tableName,
    instanceId,
    layoutNodeId,
    descriptor.model,
    descriptor.title,
    descriptor.locale,
    descriptor.languages,
  ]);

  const handleLocaleChange = useCallback(
    async (event: SelectChangeEvent<string>) => {
      const next = event.target.value;
      if (!connectionId || !tableName || !instanceId || !layoutNodeId || !next) return;
      setBusy(true);
      try {
        const data = (await printFormsApi.spreadsheetDesigner({
          connection_id: connectionId,
          table_name: tableName,
          instance_id: instanceId,
          layout_node_id: layoutNodeId,
          locale: next,
        })) as {
          model: FilledSpreadsheetModel;
          record_label?: string;
          locale?: string | null;
          languages?: PrintWindowLanguage[];
        };
        setModel(data.model);
        setLocale(data.locale ?? next);
        if (data.languages) setLanguages(data.languages);
        setDirty(false);
      } catch (e) {
        setToast(printErrorMessage(e, 'Failed to refill the print form'));
      } finally {
        setBusy(false);
      }
    },
    [connectionId, tableName, instanceId, layoutNodeId],
  );

  const handleCellEdit = useCallback(
    (sectionIndex: number, rowIndex: number, cellIdx: number, value: string) => {
      setModel((prev) => {
        if (!prev) return prev;
        const next: FilledSpreadsheetModel = JSON.parse(JSON.stringify(prev));
        const cell = next.sections[sectionIndex]?.rows[rowIndex]?.cells[cellIdx];
        if (cell) cell.value = value;
        return next;
      });
      setDirty(true);
    },
    [],
  );

  const updatePageSettings = useCallback((patch: Partial<PageSettings>) => {
    setModel((prev) =>
      prev ? { ...prev, page_settings: { ...prev.page_settings, ...patch } } : prev,
    );
  }, []);

  const handleBrowserPrint = useCallback(async () => {
    if (!model) return;
    setBusy(true);
    try {
      const data = (await printFormsApi.renderModel({ model, format: 'html' })) as {
        content: string;
      };
      const frame = printFrameRef.current;
      if (!frame) return;
      frame.srcdoc = data.content;
      await new Promise((resolve) => {
        frame.onload = resolve;
      });
      frame.contentWindow?.print();
    } catch (e) {
      setToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [model]);

  const handlePdf = useCallback(async () => {
    if (!model) return;
    setBusy(true);
    try {
      const data = (await printFormsApi.renderModel({
        model,
        format: 'pdf',
        connection_id: connectionId,
        attachment_ids: printAttachments.selectedAttachments.map((a) => a.id),
      })) as {
        content: string;
        filename: string;
      };
      downloadBase64(data.content, data.filename, 'application/pdf');
    } catch (e) {
      setToast(printErrorMessage(e, 'Failed to export PDF'));
    } finally {
      setBusy(false);
    }
  }, [model, connectionId, printAttachments.selectedAttachments]);

  const handleXlsx = useCallback(async () => {
    if (!model) return;
    setBusy(true);
    try {
      const data = (await printFormsApi.exportXlsx({ model })) as {
        content: string;
        filename: string;
      };
      downloadBase64(
        data.content,
        data.filename,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
    } catch (e) {
      setToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [model]);

  const settings = model?.page_settings;

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="error">
          <Typography fontWeight={600}>
            {t('printWindow.loadError', 'Could not open the print form')}
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.200',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
        <CommandBar
          toolbar
          primary={[
            {
              id: 'Print',
              label: t('printWindow.print', 'Print'),
              testId: 'print-window-print',
              disabled: !model || busy,
              onClick: () => {
                void handleBrowserPrint();
              },
            },
          ]}
          utilities={
            dirty ? (
              <Chip
                size="small"
                color="warning"
                label={t('printWindow.edited', 'Edited')}
                data-testid="print-dirty-badge"
              />
            ) : undefined
          }
          overflow={[
            {
              id: 'PDF',
              label: 'PDF',
              icon: <PictureAsPdfIcon fontSize="small" />,
              testId: 'print-window-pdf',
              disabled: !model || busy,
              onClick: () => {
                void handlePdf();
              },
            },
            {
              id: 'Excel',
              label: 'Excel',
              icon: <GridOnIcon fontSize="small" />,
              testId: 'print-window-xlsx',
              disabled: !model || busy,
              onClick: () => {
                void handleXlsx();
              },
            },
            ...(printAttachments.attachments.length > 0
              ? [
                  {
                    id: 'Attachments',
                    label:
                      t('printWindow.attachments', 'Attachments') +
                      (printAttachments.selected.size > 0
                        ? ` (${printAttachments.selected.size})`
                        : ''),
                    icon: <AttachFileIcon fontSize="small" />,
                    testId: 'print-window-attachments',
                    onClick: () => setAttachmentsPanelOpen((v) => !v),
                  },
                ]
              : []),
            {
              id: 'Email',
              label: t('printWindow.email', 'Email'),
              icon: <EmailIcon fontSize="small" />,
              testId: 'print-window-email',
              disabled: !model || busy,
              onClick: () => setEmailDialogOpen(true),
            },
            {
              id: 'Page',
              label: t('printWindow.pageSetup', 'Page'),
              icon: <SettingsIcon fontSize="small" />,
              testId: 'print-window-page-setup',
              disabled: !model,
              onClick: () => setPageSetupOpen(true),
            },
          ]}
          moreExtras={
            <>
              {languages.length >= 2 && (
                <Box sx={{ px: 1.5, py: 0.75 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="print-window-language-label">
                      {t('printWindow.language', 'Language')}
                    </InputLabel>
                    <Select
                      labelId="print-window-language-label"
                      label={t('printWindow.language', 'Language')}
                      value={locale ?? ''}
                      onChange={handleLocaleChange}
                      disabled={!model || busy}
                      inputProps={{ 'aria-label': 'Language' }}
                      data-testid="print-window-language"
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box sx={{ px: 1.5, py: 0.5 }}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={editMode}
                      onChange={(e) => setEditMode(e.target.checked)}
                      data-testid="print-window-edit-toggle"
                    />
                  }
                  label={t('printWindow.edit', 'Edit')}
                />
              </Box>
              <Box sx={{ px: 1.5, pb: 1, width: 220 }}>
                <Slider
                  size="small"
                  min={50}
                  max={200}
                  step={10}
                  value={zoomPct}
                  onChange={(_e, v) => setZoomPct(v as number)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                />
              </Box>
            </>
          }
        />
      </Box>

      {printAttachments.error && (
        <Alert severity="warning" sx={{ mx: 2, mt: 1 }}>
          {t('printWindow.attachmentsLoadError', 'Could not load attached files for this record')}: {printAttachments.error}
        </Alert>
      )}

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!model ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <SpreadsheetView
                model={model}
                zoomPct={zoomPct}
                editMode={editMode}
                onCellEdit={handleCellEdit}
              />
              {printAttachments.selectedAttachments.length > 0 && connectionId && (
                <Box sx={{ p: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t('printWindow.attachedFiles', 'Attached files')} (
                    {printAttachments.selectedAttachments.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {t(
                      'printWindow.attachedFilesCaption',
                      'Only PDF and image files are merged into the printed document. Other selected files are attached to email only.',
                    )}
                  </Typography>
                  {printAttachments.selectedAttachments.map((att) => (
                    <PrintAttachmentPreview key={att.id} connectionId={connectionId} attachment={att} />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
        {attachmentsPanelOpen && (
          <PrintAttachmentsPanel
            attachments={printAttachments.attachments}
            selected={printAttachments.selected}
            onToggle={printAttachments.toggle}
            onClose={() => setAttachmentsPanelOpen(false)}
          />
        )}
      </Box>

      <iframe ref={printFrameRef} title="print-frame" style={{ display: 'none' }} />

      <Dialog open={pageSetupOpen} onClose={() => setPageSetupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('printWindow.pageSetupTitle', 'Page setup')}</DialogTitle>
        {settings && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={settings.orientation}
                onChange={(_e, v) => v && updatePageSettings({ orientation: v })}
              >
                <ToggleButton value="portrait">
                  {t('printWindow.portrait', 'Portrait')}
                </ToggleButton>
                <ToggleButton value="landscape">
                  {t('printWindow.landscape', 'Landscape')}
                </ToggleButton>
              </ToggleButtonGroup>
              <Stack direction="row" spacing={1}>
                {(['top', 'bottom', 'left', 'right'] as const).map((side) => (
                  <TextField
                    key={side}
                    size="small"
                    type="number"
                    label={t(`printWindow.margin_${side}`, side)}
                    value={settings.margins_mm[side]}
                    inputProps={{ min: 0, max: 50 }}
                    onChange={(e) =>
                      updatePageSettings({
                        margins_mm: {
                          ...settings.margins_mm,
                          [side]: Math.max(0, Number(e.target.value) || 0),
                        },
                      })
                    }
                  />
                ))}
              </Stack>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('printWindow.scale', 'Print scale')}: {settings.scale_pct}%
                </Typography>
                <Slider
                  size="small"
                  min={25}
                  max={200}
                  step={5}
                  value={settings.scale_pct}
                  onChange={(_e, v) => updatePageSettings({ scale_pct: v as number })}
                />
              </Box>
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setPageSetupOpen(false)}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      {model && connectionId && (
        <EmailPrintDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          connectionId={connectionId}
          model={model}
          mergeableSelected={printAttachments.mergeableSelected}
          otherSelected={printAttachments.otherSelected}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
}
